/**
 * CallSidebarV2 — Calls filter sidebar using SmartSidebar (same pattern as LeadSidebarV2 / CallSidebar).
 * Used inline with CallListViewV2: Toolbar on top, then Sidebar | Table.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Box, Tooltip, IconButton, Collapse, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import EnhancedAvatar from '../../components/common/EnhancedAvatar';
import { useCall } from '../../contexts/CallContext';
import { getStageConfig as STAGE_CFG, getStatusConfig as STATUS_CFG } from '../../utils/lead_helper_v2';
import { getCallStatusColor } from '../../utils/helper';
import {
  SmartSidebar,
  SmartSidebarHeader,
  SIDEBAR_THEME,
} from '../../components/common/SmartSidebar';

const SECTION_ORDER = ['all', 'my_calls', 'status', 'insight', 'lead_stage', 'unlinked'];

const CallSidebarV2 = ({
  width = 200,
  collapsed = false,
  onToggleCollapse,
  statusCounts,
  user,
  embedded = false,
}) => {
  const { setAppliedFilters, selectedStatus, setSelectedStatus } = useCall();
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedChild, setExpandedChild] = useState(null);
  const T = SIDEBAR_THEME;

  const findParentSectionId = (sections, targetId) => {
    for (const section of sections) {
      if (section.id === targetId) return section.id;
      if (section.children) {
        for (const child of section.children) {
          if (child.id === targetId) return section.id;
          if (child.children) {
            for (const grandChild of child.children) {
              if (grandChild.id === targetId) return section.id;
            }
          }
        }
      }
    }
    return null;
  };

  const findImmediateChildId = (sections, targetId) => {
    for (const section of sections) {
      for (const child of section.children || []) {
        if (child.id === targetId) return child.id;
        for (const grandChild of child.children || []) {
          if (grandChild.id === targetId) return child.id;
        }
      }
    }
    return null;
  };

  const handleToggleSection = (sectionId) => {
    setExpandedSection((prev) => (prev === sectionId ? null : sectionId));
  };

  const SECTION_DOT_COLORS = {
    all:       '#2563eb',  // blue
    my_calls:  T.green,    // green
    unlinked:  '#7c3aed',  // purple
    status:    '#9ca3af',  // gray (parent; children use their own status color)
    insight:   '#0891b2',  // cyan
    lead_stage:'#9ca3af',  // gray (parent; children use stage color)
  };

  const INSIGHT_CHILD_COLORS = {
    incoming: '#16a34a',
    outgoing: '#1d4ed8',
    missed:   '#ea580c',
  };

  const getDotColor = (sectionId, itemId) => {
    if (sectionId === 'lead_stage') {
      return STAGE_CFG(itemId).dot;
    }
    if (sectionId === 'status') {
      return getCallStatusColor(itemId)?.color || T.muted;
    }
    if (sectionId === 'insight') {
      return INSIGHT_CHILD_COLORS[itemId] || SECTION_DOT_COLORS.insight;
    }
    return SECTION_DOT_COLORS[itemId] || T.muted;
  };

  const getGrandchildDotColor = (childId, grandChildId) => {
    // grandChildId is like "closure_closed_won" or "new_not_contacted"
    const parts = String(grandChildId || '').split('_');
    const stage = parts[0];
    const subKey = parts.slice(1).join('_');
    if (stage === 'closure') {
      return STATUS_CFG(subKey).dot;
    }
    if (stage === 'new' && subKey === 'not_contacted') {
      return STATUS_CFG('assigned').dot;
    }
    if (stage === 'junk') {
      const map = { not_interested: 'not_interested', not_looking: 'not_looking_currently', cold_lead: 'cold_lead', all: 'cold_lead' };
      return STATUS_CFG(map[subKey] || subKey).dot;
    }
    return STAGE_CFG(stage).dot;
  };

  const findFilters = (sections, statusId) => {
    for (const section of sections) {
      if (section.id === statusId) {
        if (statusId === 'my_calls' && user?.id) return { agent_ids: [user.id] };
        return section.filters;
      }
      for (const child of section.children || []) {
        if (child.id === statusId) return child.filters;
        for (const grandChild of child.children || []) {
          if (grandChild.id === statusId) return grandChild.filters;
        }
      }
    }
    return {};
  };

  const handleStatusFilter = (statusId) => {
    setSelectedStatus(statusId);
    const parentId = findParentSectionId(callSections, statusId);
    if (parentId) setExpandedSection(parentId);
    const childId = findImmediateChildId(callSections, statusId);
    if (childId) setExpandedChild(childId);
    setAppliedFilters(findFilters(callSections, statusId));
  };

  const callSections = useMemo(() => {
    if (!statusCounts || Object.keys(statusCounts).length === 0) return [];

    const sections = [];
    SECTION_ORDER.forEach((sectionKey) => {
      const sectionData = statusCounts[sectionKey];
      if (!sectionData) return;

      if (sectionKey === 'all' || sectionKey === 'my_calls') {
        sections.push({
          id: sectionKey,
          label: sectionData.label,
          count: Number(sectionData.count || 0),
          children: [],
          filters: sectionData.filters || {},
        });
      } else if (sectionKey === 'unlinked') {
        sections.push({
          id: sectionKey,
          label: sectionData.label,
          count: Number(sectionData.count || 0),
          children: [],
          filters: { unlinked: true },
        });
      } else if (sectionData.filters && Object.keys(sectionData.filters).length > 0) {
        const children = Object.entries(sectionData.filters).map(([key, childData]) => ({
          id: key,
          label: childData.label || key,
          count: Number(childData.count || 0),
          filters: { [sectionData.field_name]: [key] },
          children: childData.filters
            ? Object.entries(childData.filters).map(([subKey, subData]) => ({
                id: `${key}_${subKey}`,
                label: subData.label || subKey,
                count: Number(subData.count || 0),
                // backend can provide explicit filter payload for nested items
                filters: subData.filters || { [sectionData.field_name]: [key] },
              }))
            : [],
        }));
        const childrenSum = children.reduce((acc, c) => acc + (c.count || 0), 0);
        const sectionCount = Number(sectionData.count ?? 0);
        sections.push({
          id: sectionKey,
          label: sectionData.label,
          count: sectionCount > 0 ? sectionCount : childrenSum,
          children,
          filters: sectionData.filters || {},
        });
      }
    });
    return sections;
  }, [statusCounts]);

  useEffect(() => {
    if (selectedStatus) {
      const parentId = findParentSectionId(callSections, selectedStatus);
      if (parentId) setExpandedSection(parentId);
      const childId = findImmediateChildId(callSections, selectedStatus);
      if (childId) setExpandedChild(childId);
    }
  }, [selectedStatus, callSections]);

  const toggleButtonStyles = {
    position: 'absolute',
    top: 10,
    right: -12,
    width: 24,
    height: 24,
    backgroundColor: '#fff',
    border: `1px solid ${T.border}`,
    borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    zIndex: 10,
    p: 0,
    '&:hover': { backgroundColor: T.bodyBg },
  };

  if (!embedded && collapsed) {
    return (
      <Box
        sx={{
          width: 12,
          borderRight: `1px solid ${T.border}`,
          position: 'relative',
          height: '100%',
          bgcolor: T.bodyBg,
        }}
      >
        <IconButton size="small" onClick={onToggleCollapse} sx={toggleButtonStyles}>
          <EnhancedAvatar icon={<ChevronRightIcon />} size="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: embedded ? '100%' : width,
        flex: embedded ? '0 0 auto' : undefined,
        borderRight: embedded ? 'none' : `1px solid ${T.border}`,
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: embedded ? 180 : undefined,
      }}
    >
      {!embedded && (
        <Tooltip title="Collapse sidebar" placement="right">
          <IconButton size="small" onClick={onToggleCollapse} sx={toggleButtonStyles}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <SmartSidebar
        theme={T}
        width={width}
        bodyBg
        style={{ flex: 1, borderTop: `1px solid ${T.border}` }}
      >
        <SmartSidebarHeader theme={T}>Calls</SmartSidebarHeader>
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
          <List sx={{ py: 0.5, mt: 0, '& .MuiListItemButton-root': { borderRadius: 8, mx: 0.75, margin: '1px 6px', transition: 'all 0.2s ease' } }}>
          {callSections.map((section) => {
            const isParentSelected = selectedStatus === section.id;
            const hasChildren = section.children.length > 0;
            const isExpanded = expandedSection === section.id;

            return (
              <React.Fragment key={section.id}>
                <ListItemButton
                  selected={isParentSelected && !hasChildren}
                  onClick={() => hasChildren ? handleToggleSection(section.id) : handleStatusFilter(section.id)}
                  sx={{
                    py: 0.75, px: 1.5, pl: 1.5, position: 'relative', minHeight: 36, borderRadius: 8, mb: 0.25, fontWeight: 500,
                    backgroundColor: isParentSelected && !hasChildren ? T.greenSf : 'transparent',
                    borderLeft: isParentSelected && !hasChildren ? `3px solid ${T.green}` : '3px solid transparent',
                    '&.Mui-selected': { bgcolor: T.greenSf, borderLeft: `3px solid ${T.green}`, '&:hover': { bgcolor: 'rgba(10,107,62,0.12)' } },
                    '&:hover': { bgcolor: hasChildren && !isParentSelected ? 'rgba(0,0,0,0.04)' : (isParentSelected && !hasChildren ? T.greenSf : 'rgba(0,0,0,0.04)') },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, flex: 1, minWidth: 0 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: isParentSelected && !hasChildren ? T.green : getDotColor(section.id, section.id), flexShrink: 0 }} />
                    <ListItemText
                      primary={section.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isParentSelected && !hasChildren ? 700 : 500,
                        color: isParentSelected && !hasChildren ? T.green : T.ink2,
                        fontSize: '0.8125rem',
                        noWrap: true,
                      }}
                      sx={{ m: 0 }}
                    />
                  </Box>
                  {hasChildren && (
                    isExpanded
                      ? <ExpandLessIcon fontSize="small" sx={{ color: T.muted, ml: 0.5 }} />
                      : <ExpandMoreIcon fontSize="small" sx={{ color: T.muted, ml: 0.5 }} />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 0.5, minWidth: 22, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: 99, padding: '1px 7px', fontSize: 11, fontWeight: 700,
                      backgroundColor: isParentSelected && !hasChildren ? T.greenLt : T.bg,
                      color: isParentSelected && !hasChildren ? T.green : T.muted,
                    }}
                  >
                    {section.count ?? 0}
                  </Typography>
                </ListItemButton>

                {hasChildren && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 1, pr: 0 }}>
                      {section.children.map((child) => {
                        const isChildSelected = selectedStatus === child.id;
                        const hasGrandChildren = Array.isArray(child.children) && child.children.length > 0;
                        const isChildExpanded = expandedChild === child.id;
                        const statusColor = section.id === 'status' ? (getCallStatusColor(child.id)?.color || T.green) : T.green;
                        const activeColor = section.id === 'status' ? statusColor : T.green;
                        const activeBg = section.id === 'status' ? `${activeColor}1A` : T.greenSf;
                        const activeLt = section.id === 'status' ? `${activeColor}26` : T.greenLt;
                        return (
                          <React.Fragment key={child.id}>
                            <ListItemButton
                              selected={isChildSelected && !hasGrandChildren}
                              onClick={() => {
                                if (hasGrandChildren) setExpandedChild((prev) => (prev === child.id ? null : child.id));
                                else handleStatusFilter(child.id);
                              }}
                              sx={{
                                py: 0.5, pl: 1.5, pr: 1.5, margin: '1px 6px', borderRadius: 8, minHeight: 28,
                                backgroundColor: isChildSelected && !hasGrandChildren ? activeBg : 'transparent',
                                borderLeft: isChildSelected && !hasGrandChildren ? `3px solid ${activeColor}` : '3px solid transparent',
                                '&.Mui-selected': { bgcolor: activeBg, borderLeft: `3px solid ${activeColor}`, '&:hover': { bgcolor: activeBg } },
                                '&:hover': { bgcolor: (isChildSelected && !hasGrandChildren) ? activeBg : 'rgba(0,0,0,0.04)' },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, flex: 1, minWidth: 0 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: (isChildSelected && !hasGrandChildren) ? activeColor : getDotColor(section.id, child.id), flexShrink: 0 }} />
                                <ListItemText
                                  primary={child.label}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: (isChildSelected && !hasGrandChildren) ? 700 : 500,
                                    color: (isChildSelected && !hasGrandChildren) ? activeColor : T.ink2,
                                    fontSize: '0.8125rem',
                                    noWrap: true,
                                  }}
                                  sx={{ m: 0 }}
                                />
                              </Box>
                              {hasGrandChildren && (
                                isChildExpanded
                                  ? <ExpandLessIcon fontSize="small" sx={{ color: T.muted, ml: 0.5 }} />
                                  : <ExpandMoreIcon fontSize="small" sx={{ color: T.muted, ml: 0.5 }} />
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  ml: 0.5, minWidth: 22, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  borderRadius: 99, padding: '1px 7px', fontSize: 11, fontWeight: 700,
                                  backgroundColor: (isChildSelected && !hasGrandChildren) ? activeLt : T.bg,
                                  color: (isChildSelected && !hasGrandChildren) ? activeColor : T.muted,
                                }}
                              >
                                {child.count ?? 0}
                              </Typography>
                            </ListItemButton>

                            {hasGrandChildren && (
                              <Collapse in={isChildExpanded} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding sx={{ pl: 1, pr: 0 }}>
                                  {child.children.map((gc) => {
                                    const isGcSelected = selectedStatus === gc.id;
                                    return (
                                      <ListItemButton
                                        key={gc.id}
                                        selected={isGcSelected}
                                        onClick={() => handleStatusFilter(gc.id)}
                                        sx={{
                                          py: 0.5, pl: 1.5, pr: 1.5, margin: '1px 6px', borderRadius: 8, minHeight: 28,
                                          backgroundColor: isGcSelected ? T.greenSf : 'transparent',
                                          borderLeft: isGcSelected ? `3px solid ${T.green}` : '3px solid transparent',
                                          '&.Mui-selected': { bgcolor: T.greenSf, borderLeft: `3px solid ${T.green}`, '&:hover': { bgcolor: 'rgba(10,107,62,0.12)' } },
                                          '&:hover': { bgcolor: isGcSelected ? T.greenSf : 'rgba(0,0,0,0.04)' },
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, flex: 1, minWidth: 0 }}>
                                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: isGcSelected ? T.green : getGrandchildDotColor(child.id, gc.id), flexShrink: 0 }} />
                                          <ListItemText
                                            primary={gc.label}
                                            primaryTypographyProps={{
                                              variant: 'body2',
                                              fontWeight: isGcSelected ? 700 : 500,
                                              color: isGcSelected ? T.green : T.ink2,
                                              fontSize: '0.8125rem',
                                              noWrap: true,
                                            }}
                                            sx={{ m: 0 }}
                                          />
                                        </Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            ml: 0.5, minWidth: 22, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            borderRadius: 99, padding: '1px 7px', fontSize: 11, fontWeight: 700,
                                            backgroundColor: isGcSelected ? T.greenLt : T.bg,
                                            color: isGcSelected ? T.green : T.muted,
                                          }}
                                        >
                                          {gc.count ?? 0}
                                        </Typography>
                                      </ListItemButton>
                                    );
                                  })}
                                </List>
                              </Collapse>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
          </List>
          <Box sx={{ minHeight: 100 }} />
        </Box>
      </SmartSidebar>
    </Box>
  );
};

export default CallSidebarV2;
